import { useEffect } from "react";
import { CONTACT, COMPANY } from "./plans";
import "./LegalPages.css";

/**
 * Pages légales publiques (URL directes : /mentions-legales, /confidentialite,
 * /cgv) et vue 404. Autonomes : pas de session, pas de navigation interne —
 * un simple en-tête avec retour à l'accueil.
 *
 * ⚠ Les champs de COMPANY marqués « à compléter » (plans.js) doivent être
 * renseignés avant toute communication commerciale.
 */

function LegalShell({ title, children }) {
  useEffect(() => {
    document.title = `${title} — RACINES`;
  }, [title]);
  return (
    <div className="legal-page">
      <header className="legal-header">
        <a href="/" className="rc-wordmark legal-brand">Racines</a>
        <a href="/" className="legal-back">← Retour à l'accueil</a>
      </header>
      <main className="legal-main">
        <h1>{title}</h1>
        {children}
        <p className="legal-updated">Dernière mise à jour : {COMPANY.legalUpdated}</p>
      </main>
      <footer className="legal-footer">
        <a href="/mentions-legales">Mentions légales</a>
        <a href="/confidentialite">Politique de confidentialité</a>
        <a href="/cgv">Conditions générales de vente</a>
      </footer>
    </div>
  );
}

function MentionsLegales() {
  return (
    <LegalShell title="Mentions légales">
      <h2>Éditeur du site</h2>
      <p>
        Le site fidelity-card.fr est édité par {COMPANY.name}
        {COMPANY.legalForm ? ` (${COMPANY.legalForm})` : ""},
        {" "}{COMPANY.address}.
        <br />SIRET : {COMPANY.siret}
        <br />Responsable de la publication : {COMPANY.publisher}
        <br />Contact : <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a> —{" "}
        <a href={`tel:${CONTACT.phone.replace(/ /g, "")}`}>{CONTACT.phone}</a>
      </p>
      <h2>Hébergement</h2>
      <p>
        Le site est hébergé par OVH SAS, 2 rue Kellermann, 59100 Roubaix,
        France — <a href="https://www.ovhcloud.com" target="_blank" rel="noopener noreferrer">ovhcloud.com</a>.
      </p>
      <h2>Propriété intellectuelle</h2>
      <p>
        L'ensemble des contenus du site (textes, visuels, logos, code) est la
        propriété de {COMPANY.name} ou fait l'objet d'une autorisation
        d'utilisation. Toute reproduction sans accord préalable est interdite.
      </p>
    </LegalShell>
  );
}

function Confidentialite() {
  return (
    <LegalShell title="Politique de confidentialité">
      <p>
        {COMPANY.name} attache une grande importance à la protection de vos
        données personnelles. La présente politique décrit les données
        collectées via le site fidelity-card.fr et le service de cartes de
        fidélité, et vos droits sur ces données (Règlement (UE) 2016/679,
        « RGPD »).
      </p>
      <h2>Responsable de traitement</h2>
      <p>
        {COMPANY.name}, {COMPANY.address} —{" "}
        <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>.
      </p>
      <h2>Données collectées</h2>
      <h3>Clients des commerces (porteurs de carte)</h3>
      <p>
        Lors de la création de votre carte de fidélité : nom, prénom, adresse
        e-mail, numéro de téléphone, date d'anniversaire (facultative), ainsi
        que l'historique des passages (tampons / points) dans le commerce
        concerné.
      </p>
      <h3>Commerçants (clients du service)</h3>
      <p>
        Lors de la souscription : identité et coordonnées du gérant, nom et
        SIRET du commerce, coordonnées de facturation. Les coordonnées
        bancaires (IBAN) sont collectées et conservées exclusivement par notre
        prestataire de paiement Stripe — elles ne transitent pas par nos
        serveurs.
      </p>
      <h2>Finalités et bases légales</h2>
      <ul>
        <li>
          Fourniture du service de carte de fidélité (création de la carte,
          mise à jour des tampons et points, affichage dans Apple Wallet /
          Google Wallet) — exécution du contrat.
        </li>
        <li>
          Envoi d'e-mails liés au programme de fidélité (récompense atteinte,
          anniversaire…) pour le compte du commerce — intérêt légitime du
          commerçant ; chaque e-mail comporte un lien de désinscription.
        </li>
        <li>Facturation et gestion de l'abonnement — exécution du contrat et obligations légales.</li>
      </ul>
      <h2>Destinataires et sous-traitants</h2>
      <p>
        Les données des porteurs de carte sont accessibles au commerce qui a
        émis la carte et à {COMPANY.name}. Nous faisons appel aux
        sous-traitants suivants : OVH (hébergement, France), Stripe (paiement),
        Apple et Google (affichage de la carte dans le portefeuille numérique
        de votre téléphone). Aucune donnée n'est vendue ni transmise à des
        tiers à des fins publicitaires.
      </p>
      <h2>Durées de conservation</h2>
      <p>
        Les données de la carte sont conservées pendant la durée de vie du
        programme de fidélité du commerce, puis supprimées. Les données de
        facturation sont conservées pendant les durées légales comptables
        (10 ans).
      </p>
      <h2>Cookies</h2>
      <p>
        Le site n'utilise que des cookies techniques de session, strictement
        nécessaires à la connexion des commerçants. Aucun cookie publicitaire
        ni traceur tiers n'est déposé — c'est pourquoi aucun bandeau de
        consentement n'est affiché.
      </p>
      <h2>Vos droits</h2>
      <p>
        Vous disposez d'un droit d'accès, de rectification, d'effacement, de
        limitation, d'opposition et de portabilité sur vos données. Pour
        l'exercer, écrivez à{" "}
        <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>. Vous pouvez
        également saisir la CNIL (<a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">cnil.fr</a>).
      </p>
    </LegalShell>
  );
}

function Cgv() {
  return (
    <LegalShell title="Conditions générales de vente">
      <h2>1. Objet</h2>
      <p>
        Les présentes conditions régissent la souscription et l'utilisation du
        service de cartes de fidélité numériques proposé par {COMPANY.name}
        {" "}aux professionnels (ci-après « le Client ») sur fidelity-card.fr.
      </p>
      <h2>2. Offres et tarifs</h2>
      <p>
        Les offres, leurs fonctionnalités et leurs tarifs sont détaillés sur la
        page <a href="/">« Nos offres »</a>. Les prix s'entendent hors taxes,
        facturés mensuellement par prélèvement SEPA via notre prestataire
        Stripe.
      </p>
      <h2>3. Souscription et durée</h2>
      <p>
        L'abonnement est conclu sans engagement de durée. Il prend effet à la
        validation du mandat de prélèvement et se renouvelle tacitement chaque
        mois. Le Client peut résilier à tout moment depuis son espace de
        gestion ou par e-mail ; la résiliation prend effet à la fin de la
        période mensuelle en cours.
      </p>
      <h2>4. Paiement et défaut de paiement</h2>
      <p>
        En cas d'échec de prélèvement non régularisé, {COMPANY.name} se
        réserve le droit de suspendre le service après notification. Les
        cartes déjà émises restent dans le téléphone des clients finaux mais
        cessent d'être mises à jour.
      </p>
      <h2>5. Obligations du Client</h2>
      <p>
        Le Client s'engage à fournir des informations exactes, à utiliser le
        service conformément à la loi (notamment au RGPD vis-à-vis de ses
        propres clients) et à ne pas détourner le service de sa finalité de
        fidélisation.
      </p>
      <h2>6. Responsabilité</h2>
      <p>
        {COMPANY.name} s'engage à fournir le service avec diligence.
        L'affichage des cartes dépend des plateformes Apple Wallet et Google
        Wallet, dont les interruptions éventuelles ne sauraient engager la
        responsabilité de {COMPANY.name}. La responsabilité de{" "}
        {COMPANY.name} est en tout état de cause limitée au montant des sommes
        versées par le Client au titre des trois derniers mois d'abonnement.
      </p>
      <h2>7. Données personnelles</h2>
      <p>
        Le traitement des données est décrit dans la{" "}
        <a href="/confidentialite">politique de confidentialité</a>. Pour les
        données de ses clients finaux, le Client demeure responsable de
        traitement ; {COMPANY.name} agit en qualité de sous-traitant.
      </p>
      <h2>8. Droit applicable</h2>
      <p>
        Les présentes conditions sont soumises au droit français. À défaut de
        résolution amiable, tout litige relève des tribunaux compétents du
        ressort du siège de {COMPANY.name}.
      </p>
    </LegalShell>
  );
}

export function LegalPage({ page }) {
  if (page === "mentions") return <MentionsLegales />;
  if (page === "privacy") return <Confidentialite />;
  return <Cgv />;
}

export function NotFound() {
  useEffect(() => {
    document.title = "Page introuvable — RACINES";
  }, []);
  return (
    <div className="legal-page notfound-page">
      <header className="legal-header">
        <a href="/" className="rc-wordmark legal-brand">Racines</a>
      </header>
      <main className="legal-main notfound-main">
        <p className="notfound-code">404</p>
        <h1>Cette page n'existe pas</h1>
        <p>
          Le lien que vous avez suivi est peut-être cassé, ou la page a été
          déplacée.
        </p>
        <a href="/" className="rc-btn rc-btn-primary">Retour à l'accueil</a>
      </main>
    </div>
  );
}
